import * as React from "react";
import { useState } from "react";
import { Control, Path, FieldValues, useController } from "react-hook-form";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
type PasswordFieldProps<T extends FieldValues> = {
  control: Control<T, object>;
  name: Path<T>;
  label: string;
  placeholder?: string;
  validatePassword?: boolean;
  onBlur?: () => void;
};

const PASSWORD_PATTERN = {
  value: /^(?=.*\d)(?=.*[!@#$%^&*])[a-zA-Z\d!@#$%^&*]{8,}$/,
  message: `Password must be 8+ characters with a letter, a digit, and a special character from !@#$%^&*`,
};

export default function PasswordField<T extends FieldValues>({
  name,
  label,
  control,
  validatePassword = false,
  placeholder = "Password",
  onBlur,
}: PasswordFieldProps<T>) {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: {
      required: `${label} is required`,
      pattern: validatePassword ? PASSWORD_PATTERN : undefined,
    },
  });
  return (
    <TextField
      id="password-input"
      data-testid="password-field"
      label={label}
      type={showPassword ? "text" : "password"}
      fullWidth
      placeholder={placeholder}
      InputProps={{
        startAdornment: <LockOutlinedIcon />,
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={() => setShowPassword(!showPassword)}
              edge="end"
            >
              {showPassword ? (
                <VisibilityOffOutlinedIcon />
              ) : (
                <VisibilityOutlinedIcon />
              )}
            </IconButton>
          </InputAdornment>
        ),
      }}
      required
      {...(Boolean(error?.message)
        ? { error: true, helperText: error?.message }
        : {})}
      {...field}
      onBlur={() => {
        onBlur?.();
        field.onBlur();
      }}
    />
  );
}
